// Klubbportalen (/klubber, planer/klubbportal-plan.md): klubbene våre per butikk, med
// særidretter og klubbavtale, og søk i klubber, trenere, innkjøpsansvarlige og innlegg.
// Alle ansatte leser; leder og klubbansvarlig kan opprette klubber.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tilbake, Sidetopp, feltKlasse } from '../../ny-kundesamhandling/Skjema';
import { FARGER } from '../../ny-kundesamhandling/handlinger';
import Ikon from '../../ny-kundesamhandling/Ikon';
import AvtaleLogo from '../../components/AvtaleLogo';
import { useKanSkriveKlubb } from '../../employeeAccess';
import { BUTIKKER, butikkNavn, valgtButikk, klubbApi, rolleNavn, formatDato } from './klubbApi';
import { KlubbSkjema } from './Skjemaer';

const merke = 'inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-semibold';

function KlubbKort({ klubb }) {
  const antall = (rolle) => klubb.personer.filter((p) => p.rolle === rolle).length;
  return (
    <Link
      to={`/klubber/${klubb._id}`}
      className="group flex h-full w-full flex-col gap-2 rounded-2xl border-2 border-slate-200 bg-white p-3 transition hover:border-sky-300 hover:bg-sky-50/50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
    >
      <span className="flex items-start gap-2">
        <AvtaleLogo agreement={{ name: klubb.navn, logo: klubb.avtale?.logo }} />
        <span className="min-w-0 flex-1">
          <span lang="no" className="block text-base font-bold leading-tight text-slate-900 break-words hyphens-auto">
            {klubb.navn}
            {klubb.kortnavn && <span className="font-semibold text-slate-600"> ({klubb.kortnavn})</span>}
          </span>
          <span className="mt-1.5 flex flex-wrap gap-1.5">
            {klubb.sarIdretter.map((s) => (
              <span key={s._id} className={`${merke} bg-sky-100 text-sky-900`}>{s.navn}</span>
            ))}
            {klubb.aktiv === false && <span className={`${merke} bg-slate-200 text-slate-800`}>Ikke aktiv</span>}
          </span>
        </span>
      </span>
      <span className="block text-slate-700 line-clamp-2 [overflow-wrap:anywhere]">
        {klubb.avtale ? klubb.avtale.discountDetails : 'Ingen klubbavtale koblet'}
      </span>
      <span className="mt-auto flex items-center justify-between gap-2 text-sm text-slate-600">
        <span className="flex flex-wrap gap-x-3 gap-y-1">
          <span>{antall('trener')} trenere</span>
          <span>{antall('innkjopsansvarlig')} innkjøpsansv.</span>
          <span>{klubb.antallInnlegg} innlegg{klubb.sisteInnlegg ? `, sist ${formatDato(klubb.sisteInnlegg)}` : ''}</span>
        </span>
        <Ikon navn="pil" className="w-5 h-5 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-indigo-700" />
      </span>
    </Link>
  );
}

function Treff({ sok }) {
  const { innlegg, personer } = sok;
  if (!innlegg.length && !personer.length) return null;
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {personer.length > 0 && (
        <section aria-labelledby="kp-treff-personer" className="min-w-0 space-y-2">
          <h3 id="kp-treff-personer" className="font-bold text-slate-900">Personer ({personer.length})</h3>
          <ul className="space-y-1.5">
            {personer.map((p) => (
              <li key={p._id} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <Link to={`/klubber/${p.klubbId}`} className="font-semibold text-indigo-700 underline-offset-2 hover:underline [overflow-wrap:anywhere]">
                  {p.navn}
                </Link>
                <span className="block text-sm text-slate-700 [overflow-wrap:anywhere]">
                  {rolleNavn(p.rolle)}{p.tittel ? `, ${p.tittel}` : ''} · {p.klubbNavn}{p.sarIdrett ? `, ${p.sarIdrett}` : ''}
                </span>
                {(p.telefon || p.epost) && (
                  <span className="block text-sm text-slate-700 [overflow-wrap:anywhere]">{[p.telefon, p.epost].filter(Boolean).join(' · ')}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {innlegg.length > 0 && (
        <section aria-labelledby="kp-treff-innlegg" className="min-w-0 space-y-2">
          <h3 id="kp-treff-innlegg" className="font-bold text-slate-900">Innlegg ({innlegg.length})</h3>
          <ul className="space-y-1.5">
            {innlegg.map((i) => (
              <li key={i._id} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <Link to={`/klubber/${i.klubbId}#innlegg-${i._id}`} className="font-semibold text-indigo-700 underline-offset-2 hover:underline [overflow-wrap:anywhere]">
                  {i.tittel}
                </Link>
                <span className="block text-sm text-slate-700">
                  {i.klubbNavn}{i.sarIdrett ? `, ${i.sarIdrett}` : ''} · {formatDato(i.createdAt)}{i.arkivert?.dato ? ' · arkivert' : ''}
                </span>
                {i.tekst && <span className="block text-sm text-slate-700 line-clamp-2 [overflow-wrap:anywhere]">{i.tekst}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default function Klubber() {
  const [klubber, setKlubber] = useState([]);
  const [laster, setLaster] = useState(true);
  const [feil, setFeil] = useState('');
  const [q, setQ] = useState('');
  const [sok, setSok] = useState({ innlegg: [], personer: [], klubber: [] });
  const [visNy, setVisNy] = useState(false);
  const kanSkrive = useKanSkriveKlubb();
  const navigate = useNavigate();
  const minButikk = valgtButikk();

  useEffect(() => {
    klubbApi('/klubber')
      .then(setKlubber)
      .catch(() => setFeil('Klarte ikke å hente klubbene. Last siden på nytt om litt.'))
      .finally(() => setLaster(false));
  }, [kanSkrive]); // på nytt når en leder velger seg selv: antallet tar med lederinnleggene

  // Søk i personer og innlegg på serveren, litt etter at brukeren har sluttet å skrive.
  useEffect(() => {
    const tekst = q.trim();
    if (tekst.length < 2) {
      setSok({ innlegg: [], personer: [], klubber: [] });
      return undefined;
    }
    const t = setTimeout(() => {
      klubbApi(`/sok?q=${encodeURIComponent(tekst)}`).then(setSok).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, kanSkrive]);

  const filtrert = useMemo(() => {
    const tekst = q.trim().toLowerCase();
    if (tekst.length < 2) return klubber;
    const iSok = new Set(sok.klubber.map((k) => k._id));
    return klubber.filter(
      (k) => iSok.has(k._id) || [k.navn, k.kortnavn, ...k.sarIdretter.map((s) => s.navn)].some((v) => v && v.toLowerCase().includes(tekst))
    );
  }, [klubber, q, sok.klubber]);

  // Butikken man er logget inn i først, og ikke aktive klubber sist.
  const grupper = useMemo(() => {
    const rekkefolge = [...BUTIKKER].sort((a, b) => (a.verdi === minButikk ? -1 : b.verdi === minButikk ? 1 : 0));
    return rekkefolge.map((b) => ({
      ...b,
      klubber: filtrert.filter((k) => k.butikk === b.verdi).sort((x, y) => (x.aktiv === false) - (y.aktiv === false)),
    }));
  }, [filtrert, minButikk]);

  const sokAktivt = q.trim().length >= 2;
  const antallTreff = filtrert.length + sok.personer.length + sok.innlegg.length;

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <Tilbake />

      <Sidetopp ikon="folk" farge="kunde" tittel="Klubber" undertittel="Klubbene våre: avtaler, særidretter, trenere, innkjøpsansvarlige og beskjeder">
        {kanSkrive && (
          <button
            type="button"
            onClick={() => setVisNy((v) => !v)}
            aria-expanded={visNy}
            className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-2xl border-2 border-sky-200 bg-sky-50 px-3 py-1.5 font-bold text-slate-900 transition hover:bg-sky-100 active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${FARGER.kunde.ikon}`}>
              <Ikon navn="pluss" className="w-6 h-6" />
            </span>
            Ny klubb
          </button>
        )}
      </Sidetopp>

      {visNy && (
        <KlubbSkjema
          onLagret={(k) => navigate(`/klubber/${k._id}`)}
          onAvbryt={() => setVisNy(false)}
        />
      )}

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <label htmlFor="kp-sok" className="mb-1 block font-semibold text-slate-800">Søk i klubbene</label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <Ikon navn="sok" className="w-6 h-6" />
          </span>
          <input
            id="kp-sok"
            type="search"
            inputMode="search"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Klubb, særidrett, trener, mobilnummer eller ord i et innlegg"
            aria-describedby="kp-sok-status"
            className={`${feltKlasse} pl-12`}
          />
        </div>
      </div>

      <p id="kp-sok-status" role="status" className="text-slate-700 empty:hidden">
        {!laster && sokAktivt ? `${antallTreff} treff` : ''}
      </p>

      {sokAktivt && <Treff sok={sok} />}

      {laster ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter klubber …</p>
      ) : (
        grupper.map((g) => (
          <section key={g.verdi} aria-labelledby={`kp-${g.verdi}`} className="space-y-3">
            <h2 id={`kp-${g.verdi}`} className="text-lg font-bold text-slate-900">{butikkNavn(g.verdi)}</h2>
            {g.klubber.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-slate-700">
                {sokAktivt ? 'Ingen klubber passer søket.' : 'Ingen klubber registrert ennå.'}
              </p>
            ) : (
              <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {g.klubber.map((k) => (
                  <li key={k._id} className="min-w-0"><KlubbKort klubb={k} /></li>
                ))}
              </ul>
            )}
          </section>
        ))
      )}
    </div>
  );
}
